import * as React from 'react';
import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import type { FormikProps } from 'formik';
import { useFormikContext } from 'formik';

import type { ReceiptBatchFormValues } from './ReceiptBatchForm';
import { ReceiptBatchV2CommonView } from './ReceiptBatchV2CommonView';
import { useFeatureFlag } from '@pflegenavi/frontend/feature-flags';
import type { RecurringItemDto } from '@pflegenavi/shared/api';
import { FeatureFlag, FeatureFlagStatus } from '@pflegenavi/shared/api';
import { redistributeFees } from './utils/receiptFees';
import { Iconify, Label, Stack } from '@pflegenavi/web-components';
import { Card, Divider, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface ReceiptBatchCommonProps<T extends ReceiptBatchFormValues> {
  renderReceiptBatchCommonForm: (
    formik: FormikProps<T>,
    recurringItem: RecurringItemDto | undefined
  ) => React.ReactNode;
  renderReceiptBatchImageInput: (formik: FormikProps<T>) => React.ReactNode;
  renderCashLink?: (
    formik: FormikProps<T>,
    newBatch: boolean,
    onAddCashTransactionGroup?: (
      separateFeeAmount: number,
      immediateFeeAmount: number,
      immediateFeeTracked: boolean,
      separateFeePercentage: number | undefined
    ) => void,
    onSelectCashTransactionGroup?: (
      separateFeeAmount: number | undefined,
      immediateFeeAmount: number | undefined
    ) => void,
    onRemove?: () => void
  ) => React.ReactNode;
  recurringItem: RecurringItemDto | undefined;
  newBatch: boolean;
}

export function ReceiptBatchCommonContainer<T extends ReceiptBatchFormValues>({
  renderReceiptBatchCommonForm,
  renderReceiptBatchImageInput,
  renderCashLink,
  recurringItem,
  newBatch,
}: ReceiptBatchCommonProps<T>): React.ReactNode {
  const formik = useFormikContext<T>();

  const cashListEnabled =
    useFeatureFlag(FeatureFlag.CoinList) === FeatureFlagStatus.Enabled;

  const ReceiptBatchCommonForm = useMemo(() => {
    return renderReceiptBatchCommonForm(formik, recurringItem);
  }, [formik, recurringItem, renderReceiptBatchCommonForm]);

  const ReceiptBatchImageInput = useMemo(() => {
    return renderReceiptBatchImageInput(formik);
  }, [formik, renderReceiptBatchImageInput]);

  const onAddCashTransactionGroup = useCallback(
    (
      separateFeeAmount: number,
      immediateFeeAmount: number,
      immediateFeeTracked: boolean,
      separateFeePercentage: number | undefined
    ) => {
      const updatedReceipts = redistributeFees(
        formik.values.receipts,
        -separateFeeAmount,
        -immediateFeeAmount
      );

      formik.setFieldValue('receipts', updatedReceipts);
    },
    [formik]
  );

  const onSelectCashTransactionGroup = useCallback(
    (
      separateFeeAmount: number | undefined,
      immediateFeeAmount: number | undefined
    ) => {
      const updatedReceipts = redistributeFees(
        formik.values.receipts,
        separateFeeAmount ? -separateFeeAmount : 0,
        immediateFeeAmount ? -immediateFeeAmount : 0
      );

      formik.setFieldValue('receipts', updatedReceipts);
    },
    [formik]
  );

  const onRemoveCashTransactionGroup = useCallback(() => {
    const updatedReceipts = redistributeFees(formik.values.receipts, 0, 0);

    formik.setFieldValue('receipts', updatedReceipts);
  }, [formik]);

  const CashLink = useMemo(() => {
    if (!renderCashLink || !cashListEnabled) {
      return null;
    } else if (formik.values.earnings) {
      return <EarningsInfoCard />;
    }
    return renderCashLink(
      formik,
      newBatch,
      onAddCashTransactionGroup,
      onSelectCashTransactionGroup,
      onRemoveCashTransactionGroup
    );
  }, [
    formik,
    renderCashLink,
    cashListEnabled,
    onAddCashTransactionGroup,
    onSelectCashTransactionGroup,
    onRemoveCashTransactionGroup,
    newBatch,
  ]);

  return (
    <ReceiptBatchV2CommonView
      receiptsCount={formik.values.receipts.length}
      totalAmount={formik.values.receipts.reduce(
        (acc, receipt) => acc + (receipt.amount ?? 0),
        0
      )}
      totalAdditionalFeeAmount={formik.values.receipts.reduce(
        (acc, receipt) => acc + (receipt.additionalFeeAmount ?? 0),
        0
      )}
      totalIncludedFeeAmount={formik.values.receipts.reduce(
        (acc, receipt) => acc + (receipt.includedFeeAmount ?? 0),
        0
      )}
      ReceiptBatchCommonForm={ReceiptBatchCommonForm}
      ReceiptBatchImageInput={ReceiptBatchImageInput}
      CashLink={CashLink}
    />
  );
}

export const EarningsInfoCard: FC = () => {
  const { t } = useTranslation();

  return (
    <Grid item xs={12} md={6} lg={3.5}>
      <Card sx={{ p: 2 }}>
        <Stack direction="column" gap={1}>
          <Stack direction="row" gap={1} alignItems="center">
            <Typography variant="h6">
              {t('receipt.additional-information')}
            </Typography>
          </Stack>
          <Divider />
          <Stack direction="row" justifyContent="space-between">
            <Stack direction="row" alignItems="center" gap={1}>
              <Iconify
                icon="eva:trending-up-fill"
                sx={{ color: 'text.secondary' }}
                width={18}
                height={18}
              />
              <Typography fontSize={14} color="text.primary">
                {t('receipts.earnings-indicator')}
              </Typography>
            </Stack>
            <Label color="success">{t('common.yes')}</Label>
          </Stack>
        </Stack>
      </Card>
    </Grid>
  );
};
